import { AemConstants } from '../../../../interfaces/src/lib/enums/index';
import { AemNode, TransformedNode } from '../../../../interfaces/src/lib/interfaces/index';

/**
 * AEM JSON payload transformer
 *
 * When given AEM data, will re-create the tree of components
 * with properties to be used for rendering to the screen.
 *
 *
 * The basic structure of an AEM node is
 *
 * {
 *   type: 'aem/resource/type',
 *   properties: {},
 *   nodes: []
 * }
 *
 * Given an AEM sample payload of:
 *
 * {
 *    ":items":{
 *       "searchbarcontainer":{
 *          "dataComponentName":"searchbarcontainer--658625613",
 *          "id":"container-fea38a2a64",
 *          "layout":"SIMPLE",
 *          "dataLayer":{
 *             "container-fea38a2a64":{
 *                "@type":"core/wcm/components/container/v1/container",
 *                "repo:modifyDate":"2024-05-19T23:09:20Z"
 *             }
 *          },
 *          ":items":{
 *             "searchbutton":{
 *                "dataComponentName":"searchbutton--438988874",
 *                "id":"button-a90a2c5bca",
 *                "buttonLink":{
 *                   "valid":false
 *                },
 *                "text":"prova buttons",
 *                "dataLayer":{
 *                   "button-a90a2c5bca":{
 *                      "@type":"core/wcm/components/button/v2/button",
 *                      "repo:modifyDate":"2024-05-14T21:33:56Z",
 *                      "dc:title":"prova buttons"
 *                   }
 *                },
 *                ":type":"b2c-website-foundation/components/searchbar/searchbutton"
 *             }
 *          },
 *          ":itemsOrder":[
 *             "searchbutton"
 *          ],
 *          ":type":"b2c-website-foundation/components/searchbar/searchbarcontainer"
 *       }
 *    }
 * }
 *
 * The transformer will generate the following tree structure:
 *
 * {
 *    "type":"b2c-website-foundation/components/searchbar/searchbarcontainer",
 *    "properties":{
 *       "dataComponentName":"searchbarcontainer--658625613",
 *       "id":"container-fea38a2a64",
 *       "layout":"SIMPLE",
 *       "dataLayer":{
 *          "container-fea38a2a64":{
 *             "@type":"core/wcm/components/container/v1/container",
 *             "repo:modifyDate":"2024-05-19T23:09:20Z"
 *          }
 *       }
 *    },
 *    "nodes":[
 *       {
 *          "type":"b2c-website-foundation/components/searchbar/searchbutton",
 *          "properties":{
 *             "dataComponentName":"searchbutton--438988874",
 *             "id":"button-a90a2c5bca",
 *             "buttonLink":{
 *                "valid":false
 *             },
 *             "text":"prova buttons",
 *             "dataLayer":{
 *                "button-a90a2c5bca":{
 *                   "@type":"core/wcm/components/button/v2/button",
 *                   "repo:modifyDate":"2024-05-14T21:33:56Z",
 *                   "dc:title":"prova buttons"
 *                }
 *             }
 *          },
 *          "nodes":[]
 *       }
 *    ]
 * }
 */
const UNUSED_PROPERTIES: string[] = [
  AemConstants.LAST_MODIFIED_DATE,
  AemConstants.DESIGN_PATH,
  AemConstants.HTML_PAGE_ITEMS,
  AemConstants.ITEMS,
  AemConstants.ITEMS_ORDER,
  AemConstants.ALLOWED_COMPONENTS,
  AemConstants.COLUMN_CLASS_NAMES,
];

const getResourceType = (node: AemNode): string | undefined => node?.[AemConstants.TYPE];
const hasResourceType = (node: AemNode): boolean => !!getResourceType(node);
const isNotResourceType = (prop: string): boolean => prop !== AemConstants.TYPE;
const isPropertyOf =
  (node: AemNode) =>
  (prop: string): boolean =>
    !hasResourceType(node[prop]);

/**
 * Extracts out the immediate properties of an AEM node
 */
export const extractProperties = (node: AemNode): Record<string, string | boolean | number> =>
  Object.getOwnPropertyNames(node)
    .filter(isNotResourceType)
    .filter(isPropertyOf(node))
    .reduce((properties: Record<string, string | boolean | number>, prop: string) => {
      return UNUSED_PROPERTIES.includes(prop)
        ? properties
        : Object.assign(properties, { [prop]: node[prop] });
    }, {});

/**
 * Returns children of an AEM node which have an AEM resource type
 */
export const getChildren = (node: AemNode): AemNode[] =>
  Object.keys(node)
    .map((prop) => prop === AemConstants.ITEMS && node[prop])
    .filter(Boolean)
    .flatMap((obj) => Object.values(obj as unknown as Record<string, AemNode>));

/**
 * Simplifies AEM tree structure to be used to render out AEM content
 */
export const transform = (node: AemNode = {}): TransformedNode => ({
  type: getResourceType(node),
  properties: extractProperties(node),
  nodes: getChildren(node).map(transform),
});
